import { useState, useEffect } from "react";
import { X, Rows3, Cable, Link2, Trash2, Lightbulb, Server, RadioTower, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const COLOR_HEX = {
  "Czerwony": "#EF4444", "Zielony": "#22C55E", "Niebieski": "#3B82F6", "Żółty": "#EAB308",
  "Fioletowy": "#A855F7", "Biały": "#F1F5F9", "Pomarańczowy": "#F97316", "Turkusowy": "#06B6D4",
  "Czarny": "#334155", "Brązowy": "#92400E", "Różowy": "#EC4899", "Szary": "#94A3B8",
};

export default function ODFView({ panel, role, onClose, onRefresh, onNavigate }) {
  const { t } = useI18n();
  const canEdit = role !== "odczyt";
  const [data, setData] = useState(null);
  const [cables, setCables] = useState([]);
  const [olts, setOlts] = useState([]);
  const [switches, setSwitches] = useState([]);
  const [panels, setPanels] = useState([]);
  const [sel, setSel] = useState(null); // selected port no
  const [cf, setCf] = useState({ cable_id: "", fiber_number: "" });
  const [pf, setPf] = useState({ target_type: "olt", target_id: "", target_label: "", target_port: "" });
  const [otherPanel, setOtherPanel] = useState("");
  const [suggest, setSuggest] = useState(null);
  const [tports, setTports] = useState(null);
  const [inputSel, setInputSel] = useState([]);

  const reload = () => api.get(`/patchpanels/${panel.id}/ports`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    reload();
    api.get("/cables").then((r) => setCables(r.data)).catch(() => {});
    api.get("/olt").then((r) => setOlts(r.data)).catch(() => {});
    api.get("/switches").then((r) => setSwitches(r.data)).catch(() => {});
    api.get("/patchpanels").then((r) => setPanels(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panel.id]);

  const connectCable = async () => {
    if (!cf.cable_id || !cf.fiber_number) { toast.error(t("Wybierz kabel i włókno")); return; }
    try {
      await api.post(`/patchpanels/${panel.id}/connect-cable`, { port_no: sel, cable_id: cf.cable_id, fiber_number: Number(cf.fiber_number) });
      toast.success(t("Kabel podłączony do portu"));
      setCf({ cable_id: "", fiber_number: "" }); reload(); onRefresh && onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || t("Błąd połączenia")); }
  };
  const doPatch = async () => {
    const body = { port_no: sel, target_type: pf.target_type, target_id: pf.target_id, target_label: pf.target_label, target_port: pf.target_port || null };
    try {
      await api.post(`/patchpanels/${panel.id}/patch`, body);
      toast.success(t("Patch cord ustawiony")); setPf({ target_type: "olt", target_id: "", target_label: "", target_port: "" }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || t("Błąd połączenia")); }
  };
  const clearPort = async () => {
    try { await api.post(`/patchpanels/${panel.id}/clear-port`, { port_no: sel }); toast.success(t("Port wyczyszczony")); reload(); onRefresh && onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Błąd połączenia")); }
  };
  const loadSuggest = async () => {
    if (!otherPanel) { toast.error(t("Wybierz drugą przełącznicę")); return; }
    try { const { data: r } = await api.get(`/patchpanels/${panel.id}/suggest-patch`, { params: { other: otherPanel } }); setSuggest(r); }
    catch (e) { toast.error(e.response?.data?.detail || t("Błąd połączenia")); }
  };
  const applySuggest = async () => {
    try { const { data: r } = await api.post(`/patchpanels/${panel.id}/patch-auto`, { other: otherPanel }); toast.success(`${t("Utworzono patch cordy:")} ${r.created}`); setSuggest(null); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Błąd połączenia")); }
  };

  useEffect(() => {
    if ((pf.target_type === "patchpanel" || pf.target_type === "olt" || pf.target_type === "switch") && pf.target_id) {
      api.get(`/patchpanels/${panel.id}/target-ports`, { params: { type: pf.target_type, id: pf.target_id } })
        .then((r) => setTports(r.data.ports || [])).catch(() => setTports(null));
    } else setTports(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pf.target_type, pf.target_id, panel.id]);

  const populateInputs = async (confirm = false) => {
    if (!inputSel.length) { toast.error(t("Wybierz kable wejściowe")); return; }
    try {
      const { data: r } = await api.post(`/patchpanels/${panel.id}/populate-inputs`, { cable_ids: inputSel, confirm });
      if (r.warning) { if (window.confirm(r.message)) return populateInputs(true); return; }
      toast.success(`${t("Zapełniono porty:")} ${r.assigned}`); reload(); onRefresh && onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || t("Błąd połączenia")); }
  };

  const disconnectCable = async (cableId, cableNumber) => {
    if (!window.confirm(`${t("Odłączyć kabel")} ${cableNumber} ${t("od tej przełącznicy? Zwolni to wszystkie jego porty.")}`)) return;
    try {
      const { data: r } = await api.post(`/patchpanels/${panel.id}/disconnect-cable`, { cable_id: cableId });
      toast.success(`${t("Odłączono kabel — zwolniono portów:")} ${r.cleared}`);
      setInputSel((s) => s.filter((x) => x !== cableId)); reload(); onRefresh && onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || t("Błąd połączenia")); }
  };

  const selPort = data?.ports.find((p) => p.no === sel);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4" data-testid="odf-view">
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Rows3 size={18} className="text-emerald-400" /> {t("Przełącznica")} {panel.number}</h3>
          <button onClick={onClose} data-testid="odf-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>
        {!data ? <div className="p-6 text-slate-500 text-sm">{t("Ładowanie…")}</div> : (
          <div className="overflow-y-auto p-5">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mb-4 text-sm">
              <span>{t("Adapter")}: <b className="font-mono">{panel.adapter_type || "-"}</b></span>
              <span>{t("Rozmiar")}: <b className="font-mono">{panel.size_u || 1}U</b></span>
              {data.cabinet_number && <span className="flex items-center gap-1.5"><Server size={14} className="text-green-400" /> {t("Szafa")} {data.cabinet_number}</span>}
              <span className="ml-auto flex items-center gap-3">
                <span className="text-green-400">{t("Wolne:")} <b className="font-mono">{data.free}</b></span>
                <span className="text-red-400">{t("Zajęte:")} <b className="font-mono">{data.occupied}</b></span>
              </span>
            </div>

            {/* Kable wejściowe → auto-zapełnianie portów */}
            {canEdit && (
              <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 mb-4" data-testid="odf-inputs">
                <div className="text-sm font-heading font-bold text-emerald-300 mb-1 flex items-center gap-2"><Cable size={15} /> {t("Kable wejściowe — auto-zapełnianie portów")}</div>
                <p className="text-[11px] text-slate-400 mb-2">{t("Wybierz kable wejściowe. Porty zapełnią się od nr 1, kolory włókien wg standardu kabla.")}{panel.input_count ? ` ${t("Maks. wejść:")} ${panel.input_count}.` : ""}</p>
                <div className="max-h-32 overflow-y-auto rounded border border-slate-700 divide-y divide-slate-800 mb-2">
                  {cables.map((c) => {
                    const connected = (data.ports || []).some((p) => p.cable_id === c.id);
                    return (
                    <label key={c.id} className={`flex items-center gap-2 px-2 py-1 text-xs ${connected ? "opacity-90 cursor-default" : "cursor-pointer hover:bg-slate-800"}`}>
                      <input type="checkbox" checked={connected || inputSel.includes(c.id)} disabled={connected} data-testid={`odf-input-${c.id}`}
                        onChange={(e) => setInputSel((s) => e.target.checked ? ((panel.input_count && s.length >= panel.input_count) ? s : [...s, c.id]) : s.filter((x) => x !== c.id))} />
                      <span>{c.number} <span className="text-slate-500">({c.fiber_count || "-"} wł.{c.fiber_standard ? `, ${c.fiber_standard}` : ""})</span>{connected ? <span className="ml-1.5 text-emerald-400 font-medium">✓ {t("podłączony")}</span> : null}</span>
                      {connected && (
                        <button type="button" onClick={(e) => { e.preventDefault(); disconnectCable(c.id, c.number); }} data-testid={`odf-disconnect-${c.id}`}
                          title={t("Odłącz kabel od przełącznicy")} className="ml-auto text-red-400 hover:text-red-300 shrink-0"><Trash2 size={13} /></button>
                      )}
                    </label>
                  );})}
                  {cables.length === 0 && <div className="text-[11px] text-slate-500 px-2 py-1">{t("Brak kabli")}</div>}
                </div>
                <button onClick={() => populateInputs(false)} data-testid="odf-populate" className="bg-emerald-600 hover:bg-emerald-500 text-white rounded px-3 py-1.5 text-sm flex items-center gap-1.5"><Cable size={14} /> {t("Zapełnij porty z wybranych kabli")}</button>
              </div>
            )}

            {/* Ports grid */}
            <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5 mb-4" data-testid="odf-ports">
              {data.ports.map((p) => {
                const active = p.no === sel;
                const patched = !!p.patch_target_id;
                return (
                  <button key={p.no} data-testid={`odf-port-${p.no}`}
                    title={patched ? `Podłączony do: ${p.patch_target_label || ""}${p.patch_target_port ? " · port " + p.patch_target_port : ""}` : (p.empty ? "Wolny" : "Zajęty")}
                    onClick={() => setSel(p.no)}
                    className={`rounded-md border px-1.5 py-1.5 text-left transition-colors ${active ? "ring-2 ring-cyan-400" : ""} ${p.empty ? "bg-green-900/15 border-green-700/40 hover:bg-green-900/30" : patched ? "bg-blue-900/25 border-blue-600/50 hover:bg-blue-900/40" : "bg-red-900/25 border-red-700/50 hover:bg-red-900/40"}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-mono text-slate-300">{p.no}</span>
                      {!p.empty && <span className="w-2 h-2 rounded-full" style={{ background: COLOR_HEX[p.fiber_color || p.patch_fiber_color] || "#64748b" }} />}
                    </div>
                    {!p.empty ? (
                      <div className="mt-0.5 leading-tight">
                        {p.cable_number != null && <div className="text-[9px] text-slate-400 truncate">{p.cable_number}/{p.fiber_number}</div>}
                        {patched && <div className="text-[9px] text-blue-300 truncate">→ {p.patch_target_label}{p.patch_target_port ? `:${p.patch_target_port}` : ""}</div>}
                      </div>
                    ) : <div className="text-[9px] text-green-400 mt-0.5">{t("Wolny")}</div>}
                  </button>
                );
              })}
            </div>

            {/* Selected port editor */}
            {canEdit && sel != null && (
              <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-4 mb-4" data-testid="odf-port-editor">
                <div className="text-sm font-heading font-bold text-cyan-300 mb-2">{t("Port")} {sel}</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-[11px] uppercase text-slate-500 mb-1 flex items-center gap-1"><Cable size={12} /> {t("Podłącz kabel (pigtail)")}</div>
                    <div className="grid grid-cols-2 gap-2">
                      <select value={cf.cable_id} onChange={(e) => setCf((f) => ({ ...f, cable_id: e.target.value }))} data-testid="odf-cable" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm">
                        <option value="">{t("— kabel —")}</option>
                        {cables.map((c) => <option key={c.id} value={c.id}>{c.number} ({c.fiber_count || "-"} wł.)</option>)}
                      </select>
                      <input type="number" min="1" value={cf.fiber_number} onChange={(e) => setCf((f) => ({ ...f, fiber_number: e.target.value }))} placeholder={t("Nr włókna")} data-testid="odf-fiber" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm" />
                    </div>
                    <button onClick={connectCable} data-testid="odf-connect-cable" className="mt-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded px-3 py-1.5 text-sm flex items-center gap-1.5"><Cable size={14} /> {t("Podłącz kabel")}</button>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase text-slate-500 mb-1 flex items-center gap-1"><Link2 size={12} /> {t("Patch cord do urządzenia")}</div>
                    <div className="grid grid-cols-2 gap-2">
                      <select value={pf.target_type} onChange={(e) => setPf((f) => ({ ...f, target_type: e.target.value, target_id: "", target_label: "" }))} data-testid="odf-patch-type" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm">
                        <option value="olt">OLT</option>
                        <option value="patchpanel">{t("Inna przełącznica")}</option>
                        <option value="switch">Switch</option>
                        <option value="other">{t("Inne")}</option>
                      </select>
                      {pf.target_type === "olt" ? (
                        <select value={pf.target_id} onChange={(e) => setPf((f) => ({ ...f, target_id: e.target.value }))} data-testid="odf-patch-target" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm">
                          <option value="">{t("— OLT —")}</option>
                          {olts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                        </select>
                      ) : pf.target_type === "switch" ? (
                        <select value={pf.target_id} onChange={(e) => setPf((f) => ({ ...f, target_id: e.target.value }))} data-testid="odf-patch-target" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm">
                          <option value="">— Switch —</option>
                          {switches.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      ) : pf.target_type === "patchpanel" ? (
                        <select value={pf.target_id} onChange={(e) => setPf((f) => ({ ...f, target_id: e.target.value }))} data-testid="odf-patch-target" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm">
                          <option value="">{t("— przełącznica —")}</option>
                          {panels.filter((x) => x.id !== panel.id).map((x) => <option key={x.id} value={x.id}>ODF {x.number}</option>)}
                        </select>
                      ) : (
                        <input value={pf.target_label} onChange={(e) => setPf((f) => ({ ...f, target_label: e.target.value }))} placeholder={t("Nazwa urządzenia")} data-testid="odf-patch-target" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm" />
                      )}
                    </div>
                    {tports && tports.length ? (
                      <select value={pf.target_port} onChange={(e) => setPf((f) => ({ ...f, target_port: e.target.value }))} data-testid="odf-patch-targetport" className="mt-2 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm">
                        <option value="">{t("— port docelowy —")}</option>
                        {tports.map((tp) => { const val = tp.key ?? tp.no; return <option key={val} value={val} disabled={tp.occupied}>{tp.label || `${t("Port")} ${tp.no}`}{tp.occupied ? ` • ${t("zajęty")}` : ` • ${t("wolny")}`}</option>; })}
                      </select>
                    ) : (
                      <input type="number" min="1" value={pf.target_port} onChange={(e) => setPf((f) => ({ ...f, target_port: e.target.value }))} placeholder={t("Port docelowy (opcj.)")} className="mt-2 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm" />
                    )}
                    <button onClick={doPatch} data-testid="odf-patch-apply" className="mt-2 bg-blue-600 hover:bg-blue-500 text-white rounded px-3 py-1.5 text-sm flex items-center gap-1.5"><Link2 size={14} /> {t("Ustaw patch cord")}</button>
                  </div>
                </div>
                {!selPort?.empty && (
                  <div className="mt-3 flex items-center gap-4">
                    {selPort?.patch_target_id && onNavigate && (selPort.patch_type === "patchpanel" || selPort.patch_type === "olt" || selPort.patch_type === "switch") && (
                      <button onClick={() => onNavigate(selPort.patch_type, selPort.patch_target_id)} data-testid="odf-goto-device" className="text-xs text-blue-300 hover:text-blue-200 inline-flex items-center gap-1"><Link2 size={12} /> {t("Przejdź do urządzenia")} → {selPort.patch_target_label}</button>
                    )}
                    <button onClick={clearPort} data-testid="odf-clear-port" className="text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1"><Trash2 size={12} /> {selPort?.patch_target_id ? t("Usuń połączenie / patch cord") : t("Wyczyść port")}</button>
                  </div>
                )}
              </div>
            )}

            {/* Suggest patch between two panels */}
            {canEdit && (
              <div className="rounded-lg border border-amber-700/40 bg-amber-950/20 p-4" data-testid="odf-suggest">
                <div className="text-sm font-heading font-bold text-amber-300 mb-1 flex items-center gap-2"><Lightbulb size={15} /> {t("Podpowiedź patch cordów (spotkanie przełącznic)")}</div>
                <p className="text-[11px] text-slate-400 mb-2">{t("Wybierz drugą przełącznicę — system podpowie, które porty połączyć patch cordami.")}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <select value={otherPanel} onChange={(e) => { setOtherPanel(e.target.value); setSuggest(null); }} data-testid="odf-suggest-other" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm">
                    <option value="">{t("— druga przełącznica —")}</option>
                    {panels.filter((x) => x.id !== panel.id).map((x) => <option key={x.id} value={x.id}>ODF {x.number}</option>)}
                  </select>
                  <button onClick={loadSuggest} data-testid="odf-suggest-btn" className="bg-amber-600 hover:bg-amber-500 text-white rounded px-3 py-1.5 text-sm flex items-center gap-1.5"><Wand2 size={14} /> {t("Podpowiedz")}</button>
                </div>
                {suggest && (
                  <div className="mt-3 text-sm">
                    <p className="text-slate-300 mb-2">{suggest.note}</p>
                    {suggest.pairs.length > 0 && (
                      <>
                        <div className="max-h-40 overflow-y-auto rounded border border-slate-700 divide-y divide-slate-800">
                          {suggest.pairs.map((pr, i) => (
                            <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs" data-testid={`odf-suggest-pair-${i}`}>
                              <span>ODF {suggest.a_number} · {t("port")} <b>{pr.a_port}</b> <span className="text-slate-500">({pr.a_cable}/{pr.a_fiber})</span></span>
                              <span className="text-amber-400">↔</span>
                              <span>ODF {suggest.b_number} · {t("port")} <b>{pr.b_port}</b> <span className="text-slate-500">({pr.b_cable}/{pr.b_fiber})</span></span>
                            </div>
                          ))}
                        </div>
                        <button onClick={applySuggest} data-testid="odf-suggest-apply" className="mt-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded px-3 py-1.5 text-sm flex items-center gap-1.5"><Link2 size={14} /> {t("Zastosuj patch cordy (auto)")}</button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
